// frontend/src/features/livestock/CompareView.tsx
// Two tank shots as a slider or side by side (design 7.2), and their full-screen
// overlay: shared by the compare page and the public share page (design 8.6).
// Under the view, zoom out / reset / zoom in for the synced zoom
// (docs/plans/2026-10-29-tank-shot-framing-design.md, 9), also on + - and 0
// while focus is in the view. "As taken" is side by side with no line up and
// a zoom of its own under each side, for a subject that has grown too much to
// share one scale (docs/plans/2026-10-30-organism-reference-reset-and-as-taken.md, 4).

import type { KeyboardEvent, ReactNode } from 'react';
import { ActionIcon, Box, Group, Modal, Stack, Text } from '@mantine/core';
import { useMediaQuery, useUncontrolled } from '@mantine/hooks';
import { IconDeviceMobileRotated, IconX } from '@tabler/icons-react';
import { CompareSideBySide } from './CompareSideBySide';
import { CompareSlider } from './CompareSlider';
import type { CompareShotImage } from './CompareImage';
import { NO_ZOOM, ZOOM_STEP, zoomAt, type CompareZoom } from './compareZoom';
import { LIGHTBOX_Z_INDEX } from './PhotoLightbox';
import { ZoomControls } from './ZoomControls';

export type CompareMode = 'side' | 'slider' | 'taken';

/** Side by side first in the tab order: the screen-reader-friendly view (design 7.2). The slider is the default. */
export const COMPARE_MODES: { value: CompareMode; label: string }[] = [
  { value: 'side', label: 'Side by side' },
  { value: 'slider', label: 'Slider' },
  { value: 'taken', label: 'As taken' },
];

/** The shot as taken: its line up left out. */
function asTaken(image: CompareShotImage): CompareShotImage {
  return { ...image, framing: null };
}

export interface CompareViewProps {
  mode: CompareMode;
  before: CompareShotImage;
  after: CompareShotImage;
  fullScreen?: boolean;
  /** Names the pair: a new pair is a new slider, measured again from the middle. */
  pairKey: string;
  position: number;
  onPositionChange: (position: number) => void;
  /** The synced zoom, when the parent holds it (so the page and full screen share it). */
  zoom?: CompareZoom;
  onZoomChange?: (zoom: CompareZoom) => void;
  onImageError?: () => void;
  /** Starts each image's alt text: "Tank shot before, 3 Mar 2026". */
  nounTitle?: string;
}

export function CompareView({
  mode,
  before,
  after,
  fullScreen = false,
  pairKey,
  position,
  onPositionChange,
  zoom: zoomProp,
  onZoomChange,
  onImageError,
  nounTitle = 'Tank shot',
}: CompareViewProps): JSX.Element {
  const [zoom, changeZoom] = useUncontrolled({
    value: zoomProp,
    defaultValue: NO_ZOOM,
    finalValue: NO_ZOOM,
    onChange: onZoomChange,
  });

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    // As taken: each side has its own zoom, so there is no one zoom for a key to change
    if (mode === 'taken' || event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.key === '+' || event.key === '=') changeZoom(zoomAt(zoom, ZOOM_STEP, 0.5, 0.5));
    else if (event.key === '-' || event.key === '_') {
      changeZoom(zoomAt(zoom, 1 / ZOOM_STEP, 0.5, 0.5));
    } else if (event.key === '0') changeZoom(NO_ZOOM);
    else return;
    event.preventDefault();
  };

  if (mode === 'taken') {
    return (
      <CompareSideBySide
        // A new pair starts with both sides unzoomed
        key={pairKey}
        before={asTaken(before)}
        after={asTaken(after)}
        fullScreen={fullScreen}
        independentZoom
        onImageError={onImageError}
        nounTitle={nounTitle}
      />
    );
  }

  const view =
    mode === 'slider' ? (
      <CompareSlider
        key={pairKey}
        before={before}
        after={after}
        fullScreen={fullScreen}
        position={position}
        onPositionChange={onPositionChange}
        zoom={zoom}
        onZoomChange={changeZoom}
        onImageError={onImageError}
        nounTitle={nounTitle}
      />
    ) : (
      <CompareSideBySide
        before={before}
        after={after}
        fullScreen={fullScreen}
        zoom={zoom}
        onZoomChange={changeZoom}
        onImageError={onImageError}
        nounTitle={nounTitle}
      />
    );
  return (
    <Stack gap={4} w="100%" onKeyDown={onKeyDown}>
      {view}
      <ZoomControls zoom={zoom} onZoomChange={changeZoom} dark={fullScreen} />
    </Stack>
  );
}

export interface CompareFullScreenProps {
  opened: boolean;
  onClose: () => void;
  /** The line at the top, beside Close: the gap and the two dates. */
  caption: ReactNode;
  /** The view, mounted only while open. */
  children: ReactNode;
}

/**
 * A Mantine Modal fullScreen rather than the element Fullscreen API, which
 * iPhone and in-app browsers lack (design 7.2). Close sits inside the safe area.
 */
export function CompareFullScreen({
  opened,
  onClose,
  caption,
  children,
}: CompareFullScreenProps): JSX.Element {
  // Only for the "Rotate for a bigger view" hint.
  const portraitPhone =
    useMediaQuery('(max-width: 991px) and (orientation: portrait)', false, {
      getInitialValueInEffect: false,
    }) ?? false;
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen
      padding={0}
      withCloseButton={false}
      zIndex={LIGHTBOX_Z_INDEX}
      title="Compare full screen"
      overlayProps={{ backgroundOpacity: 0.9, color: 'var(--mantine-color-black)', blur: 0 }}
      styles={{
        content: { backgroundColor: 'var(--rdh-lightbox-bg)' },
        header: { display: 'none' },
        body: { height: '100%', display: 'flex', flexDirection: 'column' },
      }}
    >
      <Group
        justify="space-between"
        wrap="nowrap"
        gap="xs"
        style={{
          paddingTop: 'max(8px, env(safe-area-inset-top, 8px))',
          paddingLeft: 'max(12px, env(safe-area-inset-left, 12px))',
          paddingRight: 'max(8px, env(safe-area-inset-right, 8px))',
        }}
      >
        <Box c="gray.0" style={{ minWidth: 0 }}>
          <Text size="sm" truncate style={{ fontVariantNumeric: 'tabular-nums' }}>
            {caption}
          </Text>
        </Box>
        <ActionIcon
          variant="subtle"
          color="gray.0"
          size={44}
          onClick={onClose}
          aria-label="Close full screen"
        >
          <IconX size={24} />
        </ActionIcon>
      </Group>
      <Box
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          paddingInline: 'max(16px, env(safe-area-inset-left, 16px))',
        }}
      >
        {opened && children}
      </Box>
      <Stack
        gap={6}
        align="center"
        style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom, 12px))' }}
        pt="xs"
      >
        {portraitPhone && (
          <Group gap={6} c="gray.4">
            <IconDeviceMobileRotated size={16} />
            <Text size="sm">Rotate for a bigger view</Text>
          </Group>
        )}
      </Stack>
    </Modal>
  );
}
